import React, { useEffect, useRef } from 'react';

// Palette: Warm Brown, Soft Beige, Muted Gold
const colors = ['rgba(139, 107, 74, 0.55)', 'rgba(214, 194, 168, 0.6)', 'rgba(200, 169, 126, 0.55)'];

export default function InteractiveBackground({
  particleCount = 100,
  mouseRadius = 160,
  attractionForce = 0.08,
  friction = 0.95,
  gravity = 0.05,
  particleSpeed = 0.4,
  connectParticles = true,
  connectDistance = 110,
}) {
  const canvasRef = useRef(null);
  const mouseRef = useRef({ x: null, y: null, targetX: null, targetY: null });
  const particlesRef = useRef([]);
  const animationFrameIdRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = (canvas.width = window.innerWidth);
    let height = (canvas.height = window.innerHeight);

    // Responsive adjustment of particle count for performance
    const isMobile = width < 768;
    const activeParticleCount = isMobile ? Math.floor(particleCount * 0.45) : particleCount;
    const activeConnectDistance = isMobile ? connectDistance * 0.75 : connectDistance;

    class Particle {
      constructor() {
        this.x = Math.random() * width;
        this.y = Math.random() * height;

        // Base coordinate (drift center)
        this.baseX = this.x;
        this.baseY = this.y;

        this.vx = (Math.random() - 0.5) * particleSpeed;
        this.vy = (Math.random() - 0.5) * particleSpeed;

        this.radius = Math.random() * 2 + 1.2;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.density = Math.random() * 20 + 8; // Force weight
      }

      update(mouseX, mouseY) {
        // Drift movement
        this.x += this.vx;
        this.y += this.vy;

        // Bounce off canvas boundaries
        if (this.x < 0 || this.x > width) this.vx = -this.vx;
        if (this.y < 0 || this.y > height) this.vy = -this.vy;

        // Update drift base coordinates
        this.baseX = this.x;
        this.baseY = this.y;

        // Mouse gravity interaction
        if (mouseX !== null && mouseY !== null) {
          const dx = mouseX - this.x;
          const dy = mouseY - this.y;
          const distance = Math.sqrt(dx * dx + dy * dy);

          if (distance < mouseRadius) {
            // Easing force: stronger attraction closer to mouse
            const force = (mouseRadius - distance) / mouseRadius;
            const directionX = (dx / distance) * force * this.density * attractionForce;
            const directionY = (dy / distance) * force * this.density * attractionForce;

            // Apply spring/gravity force towards mouse
            this.x += directionX;
            this.y += directionY;
          }
        }
      }

      draw() {
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.radius, 0, Math.PI * 2);
        ctx.fillStyle = this.color;
        ctx.fill();
      }
    }

    // Initialize particles
    const initParticles = () => {
      particlesRef.current = [];
      for (let i = 0; i < activeParticleCount; i++) {
        particlesRef.current.push(new Particle());
      }
    };

    initParticles();

    // Mouse coordinates tracking with inertia
    const handleMouseMove = (e) => {
      mouseRef.current.targetX = e.clientX;
      mouseRef.current.targetY = e.clientY;
    };

    const handleMouseLeave = () => {
      mouseRef.current.targetX = null;
      mouseRef.current.targetY = null;
    };

    // Support touch devices gently
    const handleTouchMove = (e) => {
      if (e.touches.length > 0) {
        mouseRef.current.targetX = e.touches[0].clientX;
        mouseRef.current.targetY = e.touches[0].clientY;
      }
    };

    const handleTouchEnd = () => {
      mouseRef.current.targetX = null;
      mouseRef.current.targetY = null;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd);

    // Resize handler
    const handleResize = () => {
      if (!canvas) return;
      width = canvas.width = window.innerWidth;
      height = canvas.height = window.innerHeight;
      initParticles();
    };

    window.addEventListener('resize', handleResize);

    // Smooth inertia interpolation for mouse tracking
    let currentMouseX = null;
    let currentMouseY = null;

    // Animation loop
    const animate = () => {
      ctx.clearRect(0, 0, width, height);

      // Inertia calculation for smooth cursor following
      const targetX = mouseRef.current.targetX;
      const targetY = mouseRef.current.targetY;

      if (targetX !== null && targetY !== null) {
        if (currentMouseX === null) currentMouseX = targetX;
        if (currentMouseY === null) currentMouseY = targetY;

        currentMouseX += (targetX - currentMouseX) * 0.08;
        currentMouseY += (targetY - currentMouseY) * 0.08;
      } else {
        currentMouseX = null;
        currentMouseY = null;
      }

      const particles = particlesRef.current;
      const len = particles.length;

      // Update and draw particles
      for (let i = 0; i < len; i++) {
        particles[i].update(currentMouseX, currentMouseY);
        particles[i].draw();
      }

      // Draw connection lines
      if (connectParticles) {
        for (let i = 0; i < len; i++) {
          for (let j = i + 1; j < len; j++) {
            const dx = particles[i].x - particles[j].x;
            const dy = particles[i].y - particles[j].y;
            const dist = Math.sqrt(dx * dx + dy * dy);

            if (dist < activeConnectDistance) {
              // Fade connection line as distance increases
              const alpha = (1 - dist / activeConnectDistance) * 0.06;
              ctx.strokeStyle = `rgba(139, 107, 74, ${alpha})`;
              ctx.lineWidth = 0.6;
              ctx.beginPath();
              ctx.moveTo(particles[i].x, particles[i].y);
              ctx.lineTo(particles[j].x, particles[j].y);
              ctx.stroke();
            }
          }
        }
      }

      animationFrameIdRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('resize', handleResize);
      cancelAnimationFrame(animationFrameIdRef.current);
    };
  }, [particleCount, mouseRadius, attractionForce, friction, gravity, particleSpeed, connectParticles, connectDistance]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full pointer-events-none z-0"
      style={{ mixBlendMode: 'multiply' }}
      data-testid="interactive-background"
    />
  );
}
